import { useEffect, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import Lenis from "lenis";
import Seo from "@/components/Seo";
import { DiamondMark, Grain, MonoLabel } from "@/components/legal";

const SUPPORT_EMAIL = "support@blackdiamondfacility.ca";
const PRIVACY_EMAIL = "privacy@blackdiamondfacility.ca";
const MAILTO = `mailto:${SUPPORT_EMAIL}?subject=Account%20deletion%20request&body=Please%20delete%20my%20Black%20Diamond%20account.%0A%0AName%3A%0AEmail%20on%20the%20account%3A%0APhone%20on%20the%20account%20(optional)%3A`;

const CHAPTERS = [
  {
    id: "who-we-are",
    num: "01",
    title: "Who we are",
    blocks: [
      {
        type: "p",
        text: "BLACK DIAMOND FACILITY SERVICES (\u201cBlack Diamond\u201d, \u201cwe\u201d, \u201cus\u201d) operates the Black Diamond mobile app and the website blackdiamondfacility.ca. This page explains how to delete your Black Diamond account and what happens to your data when you do.",
      },
    ],
  },
  {
    id: "delete-in-app",
    num: "02",
    title: "Delete in the app — fastest",
    blocks: [
      {
        type: "p",
        text: "You can delete your account yourself, at any time, directly in the Black Diamond app:",
      },
      {
        type: "list",
        items: [
          "Open the Black Diamond app and sign in",
          "Go to your account (Profile)",
          "Choose \u201cDelete account\u201d",
          "Type DELETE to confirm \u2014 the deletion takes effect immediately and you are signed out",
        ],
      },
      {
        type: "p",
        text: "No phone call, email, or waiting period is required when you delete in the app.",
      },
    ],
  },
  {
    id: "request-by-email",
    num: "03",
    title: "Request deletion by email",
    blocks: [
      {
        type: "p",
        text: "If you cannot access the app \u2014 or you made a booking without an account \u2014 email us from the address connected to your account or booking, and include your name and the email or phone number you used:",
      },
      {
        type: "cta",
        text: SUPPORT_EMAIL,
        href: MAILTO,
        note: "Opens your email app with a pre-filled deletion request.",
      },
      {
        type: "p",
        text: "We may need to verify that the request is genuinely yours before we process it, to protect your data from fraudulent deletion requests.",
      },
    ],
  },
  {
    id: "what-gets-deleted",
    num: "04",
    title: "What gets deleted",
    blocks: [
      {
        type: "p",
        text: "When your account is deleted \u2014 in the app or through a verified email request:",
      },
      {
        type: "list",
        items: [
          "Your user record is removed: name, email address, phone number, and profile photo",
          "Your saved properties and their photos are deleted",
          "All active sign-in sessions are revoked",
          "Your past booking and customer records are anonymized \u2014 your name, email, phone number, and address are replaced with placeholder values",
          "For completed bookings, access instructions, notes, photos, and address details are erased",
          "Uploaded photos that are no longer referenced by any record are purged from storage",
        ],
      },
    ],
  },
  {
    id: "what-may-be-retained",
    num: "05",
    title: "What may be retained",
    blocks: [
      {
        type: "p",
        text: "Some records survive account deletion in limited, non-identifying form:",
      },
      {
        type: "list",
        items: [
          "Anonymized business records \u2014 booking history with your identity removed \u2014 kept for accounting, dispute-resolution, security, and fraud-prevention purposes",
          "Limited, pseudonymous audit records of the deletion itself, for security and fraud prevention",
          "Anything we must keep to meet legal or regulatory obligations",
        ],
      },
      {
        type: "p",
        text: "We also take a verified backup of our database every night. Backups rotate on a fixed schedule \u2014 nightly backups are kept for 7 days, weekly backups for 8 weeks, and monthly backups for up to 12 months \u2014 so information deleted from the live app may remain in backups until its rotation window passes. Backups are used only for disaster recovery and are never used to return deleted personal information to live systems.",
      },
    ],
  },
  {
    id: "contact",
    num: "06",
    title: "Contact",
    blocks: [
      {
        type: "p",
        text: "Account deletion requests and questions: support@blackdiamondfacility.ca. For anything else about your personal information \u2014 access, correction, or export \u2014 contact privacy@blackdiamondfacility.ca or see our Privacy Policy at blackdiamondfacility.ca/privacy.",
      },
    ],
  },
];

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), {
    stiffness: 50,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 50,
    damping: 16,
  });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const gemY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const gemSpin = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const line = (text, i, italic = false, testId) => (
    <div key={text} className="overflow-hidden py-[0.5vw]">
      <motion.span
        data-testid={testId}
        className={`block font-serif text-[13vw] leading-[0.98] text-[#F2F2F7] sm:text-[11vw] lg:text-[8.5vw] ${
          italic ? "italic text-[#A1A1A6]" : ""
        }`}
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, ease: EASE, delay: 0.25 + i * 0.14 }}
      >
        {text}
      </motion.span>
    </div>
  );

  return (
    <header
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-[92vh] flex-col justify-end overflow-hidden px-6 pb-16 pt-32 sm:px-10 lg:px-16"
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute right-[4%] top-[16%] opacity-90 sm:right-[10%]"
        style={{
          y: gemY,
          rotate: gemSpin,
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          perspective: 800,
        }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.9 }}
      >
        <DiamondMark size={190} />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2"
        >
          <MonoLabel data-testid="delete-hero-kicker">
            BLACK DIAMOND FACILITY SERVICES
          </MonoLabel>
          <span className="hidden h-px w-16 bg-[#2C2C2E] sm:block" />
          <MonoLabel>Account Deletion</MonoLabel>
        </motion.div>

        <h1 className="m-0">
          {line("Delete your", 0, false, "delete-hero-title-line-1")}
          {line("account.", 1, true, "delete-hero-title-line-2")}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
          className="mt-10 flex flex-col gap-3 border-t border-[#2C2C2E] pt-6 sm:flex-row sm:items-end sm:justify-between"
        >
          <p
            data-testid="delete-hero-scope"
            className="max-w-md text-sm leading-relaxed text-[#A1A1A6]"
          >
            How to delete your Black Diamond account and what happens to your
            data — in the app in under a minute, or with a single email.
          </p>
          <MonoLabel data-testid="delete-hero-applies">
            Applies to — the Black Diamond app
          </MonoLabel>
        </motion.div>
      </motion.div>
    </header>
  );
};

const MARQUEE_ITEMS = [
  "Delete in the app",
  "Delete by email",
  "No phone call needed",
  "Immediate effect",
  "Your data, your call",
  "Clean work, reliable service",
];

const Marquee = () => (
  <div
    data-testid="delete-editorial-marquee"
    className="overflow-hidden border-y border-[#2C2C2E] py-5"
    aria-hidden="true"
  >
    <div className="marquee-track flex w-max items-center">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex shrink-0 items-center">
          {MARQUEE_ITEMS.map((item) => (
            <span key={`${copy}-${item}`} className="flex items-center">
              <span className="px-8 font-serif text-2xl italic text-[#636366] sm:text-3xl">
                {item}
              </span>
              <span className="inline-block h-2 w-2 rotate-45 bg-[#3A3A3C]" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const Block = ({ block }) => {
  if (block.type === "cta") {
    return (
      <div className="mt-8">
        <a
          data-testid="delete-request-email-button"
          href={block.href}
          className="group inline-flex max-w-full flex-wrap items-center gap-x-4 gap-y-1 whitespace-normal break-all border border-[#F2F2F7] px-7 py-4 text-left font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-[#050505] transition-colors duration-300 bg-[#F2F2F7] hover:bg-transparent hover:text-[#F2F2F7] sm:text-[12px] sm:tracking-[0.22em]"
        >
          {block.text}
          <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rotate-45 bg-current" />
        </a>
        <p className="m-0 mt-3 text-xs text-[#636366]">{block.note}</p>
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="m-0 mt-6 list-none space-y-4 p-0">
        {block.items.map((item) => (
          <li key={item.slice(0, 40)} className="flex gap-4">
            <span
              aria-hidden="true"
              className="mt-[9px] h-1.5 w-1.5 shrink-0 rotate-45 bg-[#8E8E93]"
            />
            <span className="text-sm leading-relaxed text-[#A1A1A6]">
              {item}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="m-0 mt-6 text-sm leading-relaxed text-[#A1A1A6] first:mt-0 sm:text-base">
      {block.text}
    </p>
  );
};

const Chapter = ({ chapter }) => (
  <motion.section
    id={chapter.id}
    data-testid={`delete-chapter-${chapter.id}`}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-90px" }}
    transition={{ duration: 0.9, ease: EASE }}
    className="relative scroll-mt-28 border-t border-[#1C1C1E] py-20 sm:py-28"
  >
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -top-6 right-0 select-none font-serif text-[26vw] leading-none text-[#0C0C0C] sm:text-[18vw] lg:text-[13rem]"
    >
      {chapter.num}
    </span>
    <div className="relative max-w-2xl">
      <MonoLabel>Chapter {chapter.num}</MonoLabel>
      <h2 className="mb-8 mt-3 font-serif text-3xl text-[#F2F2F7] sm:text-4xl">
        {chapter.title}
      </h2>
      {chapter.blocks.map((block, i) => (
        <Block key={`${chapter.id}-${i}`} block={block} />
      ))}
    </div>
  </motion.section>
);

const ChapterRail = ({ onNavigate }) => (
  <nav
    data-testid="delete-chapter-rail"
    aria-label="Chapters"
    className="sticky top-28 hidden self-start lg:block"
  >
    <MonoLabel className="mb-6 block">Index</MonoLabel>
    <ul className="m-0 list-none space-y-3 p-0">
      {CHAPTERS.map((c) => (
        <li key={c.id}>
          <a
            data-testid={`delete-chapter-link-${c.id}`}
            href={`#${c.id}`}
            onClick={(e) => onNavigate(e, c.id)}
            className="group flex items-baseline gap-3 text-[#636366] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            <span className="font-mono text-[10px] tracking-[0.2em]">
              {c.num}
            </span>
            <span className="text-[13px]">{c.title}</span>
            <span className="h-px w-0 bg-[#F2F2F7] transition-all duration-300 group-hover:w-6" />
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

export default function DeleteAccount() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollToChapter = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    const lenis = lenisRef.current;
    if (!el || !lenis) return;
    lenis.resize();
    const y = el.getBoundingClientRect().top + window.scrollY - 96;
    lenis.scrollTo(y, { duration: 1.4, force: true });
  };

  return (
    <div
      data-testid="delete-account-page"
      className="min-h-screen bg-[#050505] text-[#F2F2F7] antialiased"
      style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
    >
      <Seo
        title="Delete Your Account — Black Diamond"
        siteName="Black Diamond"
        description="How to delete your Black Diamond account and data: in the app in under a minute, or by emailing support@blackdiamondfacility.ca. What gets deleted and what may be retained."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Black Diamond",
          url: "https://blackdiamondfacility.ca",
          email: SUPPORT_EMAIL,
        }}
      />
      <Grain />

      <nav
        data-testid="delete-nav"
        className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-[#1C1C1E] bg-[#050505]/80 px-6 py-4 backdrop-blur-md sm:px-10 lg:px-16"
      >
        <a
          data-testid="delete-nav-brand-link"
          href="https://blackdiamondfacility.ca"
          className="group flex items-center gap-3"
        >
          <span className="transition-transform duration-500 group-hover:rotate-90">
            <DiamondMark size={26} />
          </span>
          <span className="leading-none">
            <span className="block font-mono text-[11px] tracking-[0.32em] text-[#F2F2F7]">
              BLACK DIAMOND
            </span>
            <span className="mt-1 block font-mono text-[8px] tracking-[0.4em] text-[#636366]">
              FACILITY SERVICE
            </span>
          </span>
        </a>
        <div className="flex items-center gap-6">
          <a
            data-testid="delete-nav-privacy-link"
            href="/privacy"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            Privacy
          </a>
          <a
            data-testid="delete-nav-terms-link"
            href="/terms"
            className="hidden font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7] sm:block"
          >
            Terms
          </a>
        </div>
      </nav>

      <Hero />
      <Marquee />

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 sm:px-10 lg:grid-cols-[240px_1fr] lg:px-16">
        <div className="pt-20 sm:pt-28">
          <ChapterRail onNavigate={scrollToChapter} />
        </div>
        <div>
          {CHAPTERS.map((c) => (
            <Chapter key={c.id} chapter={c} />
          ))}
        </div>
      </main>

      <footer
        data-testid="delete-footer"
        className="border-t border-[#1C1C1E] px-6 py-24 sm:px-10 lg:px-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <MonoLabel>Request deletion by email</MonoLabel>
          <a
            data-testid="delete-footer-email-link"
            href={MAILTO}
            className="mt-6 inline-block max-w-full break-all font-serif text-[7.5vw] leading-tight text-[#F2F2F7] underline decoration-[#3A3A3C] decoration-1 underline-offset-8 transition-colors duration-300 hover:decoration-[#F2F2F7] sm:text-5xl"
          >
            {SUPPORT_EMAIL}
          </a>
          <div className="mt-20 flex flex-col gap-4 border-t border-[#1C1C1E] pt-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <DiamondMark size={22} />
              <MonoLabel>Black Diamond — Facility Service</MonoLabel>
            </div>
            <div className="flex items-center gap-6">
              <a
                data-testid="delete-footer-privacy-link"
                href="/privacy"
                className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7]"
              >
                Privacy Policy
              </a>
              <MonoLabel data-testid="delete-footer-tagline">
                Clean work, reliable service.
              </MonoLabel>
            </div>
          </div>
        </motion.div>
      </footer>
    </div>
  );
}
